import { useQuery } from '@tanstack/react-query';
import { type MessageKey, t } from '../../i18n';
import { api } from '../../lib/api';

// Datos de /api/tickets (guía 7.1).

export const NATURES = ['incidencia', 'solicitud', 'cambio'] as const;
export type Nature = (typeof NATURES)[number];
export type StatusCategory = 'en_cola' | 'en_curso' | 'terminado' | 'cancelado';
export type Scope = 'propios' | 'asignados' | 'todos';

export interface TicketListItem {
  id: string;
  number: number;
  code: string;
  title: string;
  nature: Nature;
  organizationId: string;
  status: string;
  statusCategory: StatusCategory;
  priority: string | null;
  priorityColor: string | null;
  requesterId: string;
  assigneeId: string | null;
  slaDueAt: string | null;
  slaPausedSince: string | null;
  createdAt: string;
  updatedAt: string;
}

export type SlaState = 'sin_sla' | 'esperando_respuesta' | 'en_curso' | 'pausado' | 'cumplido' | 'incumplido' | 'cancelado';

export interface Sla {
  state: SlaState;
  targetMs: number | null;
  startAt: string | null;
  dueAt: string | null;
  pausedMs: number;
  consumedMs: number;
  remainingMs: number | null;
  ratio: number | null;
  pausedSince: string | null;
}

export interface TicketMessage {
  id: string;
  authorId: string | null;
  author: string | null;
  body: string;
  isPublic: boolean;
  createdAt: string;
}

export interface TicketDetail extends Omit<TicketListItem, 'code'> {
  code: string;
  description: string;
  statusId: string;
  categoryId: string | null;
  subcategoryId: string | null;
  priorityId: string | null;
  affectedAppId: string | null;
  category: string | null;
  subcategory: string | null;
  affectedApp: string | null;
  requester: string | null;
  assignee: string | null;
  firstResponseAt: string | null;
  resolvedAt: string | null;
  cancelledAt: string | null;
  csat: number | null;
  csatComment: string | null;
  /** Desde cuándo es el ticket activo de su responsable. */
  activeSince: string | null;
  messages: TicketMessage[];
  history: Array<{ status: string; category: StatusCategory; changedAt: string; changedBy: string | null }>;
  sla: Sla;
  /** Artículos de la Base de Conocimientos usados en la atención. */
  articles: Array<{ id: string; code: string; title: string; audience: 'todos' | 'it' }>;
  can: { manage: boolean; reply: boolean; rate: boolean };
}

export interface AppOption {
  id: string;
  name: string;
  type: string;
  parentId: string | null;
}

export interface TicketFormData {
  apps: AppOption[];
  // Solo para quien gestiona tickets (triage).
  categories?: Array<{ id: string; name: string }>;
  subcategories?: Array<{ id: string; categoryId: string; name: string; defaultNature: Nature | null; slaBaseHours: number | null }>;
  priorities?: Array<{ id: string; name: string; color: string | null; multiplier: number }>;
  statuses?: Array<{ id: string; name: string; category: StatusCategory; pausesSla: boolean }>;
}

export const useTicketForm = () => useQuery({ queryKey: ['tickets', 'formulario'], queryFn: () => api<TicketFormData>('/api/tickets/formulario') });

// Se refresca cada 30 s: las respuestas del otro lado aparecen sin recargar la página.
export const useTicket = (number: string) =>
  useQuery({ queryKey: ['tickets', 'detalle', number], queryFn: () => api<TicketDetail>(`/api/tickets/${number}`), refetchInterval: 30_000 });

export interface PanelTicket {
  id: string;
  number: number;
  code: string;
  title: string;
  organizationId: string;
  status: string;
  statusCategory: StatusCategory;
  priority: string | null;
  priorityColor: string | null;
  requesterId: string;
  requester: string | null;
  activeSince: string | null;
  createdAt: string;
  sla: Sla;
}

export interface TechPanel {
  active: PanelTicket | null;
  mine: PanelTicket[];
  queue: PanelTicket[];
  counts: { mine: number; overdue: number; dueSoon: number; paused: number; queue: number };
  warnPercent: number;
}

// Cada minuto: el tiempo restante cambia y entran tickets nuevos a la cola.
export const usePanel = () => useQuery({ queryKey: ['tickets', 'panel'], queryFn: () => api<TechPanel>('/api/tickets/panel'), refetchInterval: 60_000 });

/** Aplicaciones con su ruta ("SAGE › Reportes") para elegirlas en una lista. */
export function appLabels(apps: AppOption[]): Array<{ id: string; label: string }> {
  const byId = new Map(apps.map((a) => [a.id, a]));
  const path = (a: AppOption, depth = 0): string => {
    const parent = a.parentId ? byId.get(a.parentId) : undefined;
    return parent && depth < 5 ? `${path(parent, depth + 1)} › ${a.name}` : a.name;
  };
  return apps.map((a) => ({ id: a.id, label: path(a) })).sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

export const natureName = (n: Nature) => t(`tk.naturaleza_${n}` as MessageKey);
const TONES = { en_cola: 'amber', en_curso: 'blue', terminado: 'slate', cancelado: 'slate' } as const;
export const statusTone = (c: StatusCategory) => TONES[c];

export const formatDateTime = (iso: string) => new Date(iso).toLocaleString('es-PA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** Duración en horas hábiles: "3 h 20 min". */
export function formatDuration(ms: number): string {
  const minutes = Math.round(Math.abs(ms) / 60_000);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h === 0 ? `${m} min` : m === 0 ? `${h} h` : `${h} h ${m} min`;
}
